import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { BUBBLECHAT_WEB_PATH } from '@repo/constants';
import { setTokenOnHeader } from '@repo/services/api';
import { getCustomToken } from '@repo/services/hooks';
import { checkIsAosDevice } from '@repo/utils';
import { useQueryClient } from '@tanstack/react-query';
import { User, onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';

import { auth } from 'services/firebase';
import http, { axiosInstance } from 'services/http';
import { clearAuthToken, getTokenFromCookie, setTokenCookie } from 'utils/auth';

import { PATH } from 'routers/paths';

// 공유 쿠키 토큰(Firebase ID 토큰)의 uid. 쿠키가 없거나 해석할 수 없거나 만료됐으면 null.
// 만료된 토큰은 /auth/token 교환이 401 이라 계정을 바꿀 수 없으므로 쿠키가 없는 것과 같게 본다.
const getCookieTokenUid = () => {
  const cookieToken = getTokenFromCookie();
  if (!cookieToken) return null;
  try {
    const { sub, exp } = jwtDecode(cookieToken);
    if (exp && exp * 1000 <= Date.now()) return null;
    return sub ?? null;
  } catch {
    return null;
  }
};

const AuthGuard = ({ children }: PropsWithChildren) => {
  const cookieDomain = import.meta.env.VITE_APP_COOKIE_DOMAIN;
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const isAosDevice = checkIsAosDevice();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // 계정 전환은 마운트당 1회. /auth/token 이 쿠키와 다른 uid 를 주더라도 로그아웃↔로그인이 반복되지 않게 한다.
  const hasSwitchedAccountRef = useRef(false);

  // 로그인 왕복 후 복귀 지점. 지정한 호출자가 있을 때만 이동하고, 없으면 현재 위치를 유지한다.
  // PATH.MAIN 으로 폴백하면 인증 성공 시 항상 '/' 로 이동해 index 라우트가 다시 /dashboard 로
  // 되돌리는 왕복이 생긴다(화면 튕김 + history 에 '/' 가 쌓여 뒤로가기 막힘).
  const redirectURL = (location.state as { redirectURL?: string })?.redirectURL;
  // 로그인 성공 공통 로직
  const handleLoginInFirebaseAuth = async (user: User, token: string) => {
    setUser(user);
    setTokenCookie(token, cookieDomain);
    setTokenOnHeader(axiosInstance, auth, token);
    localStorage.setItem('isCustomLogin', 'true');
    queryClient.resetQueries();
    if (redirectURL) {
      navigate(redirectURL, { replace: true });
    }
  };

  // 로그인 페이지로 이동 (쿠키 없음 / 토큰 인증 실패 공통)
  const redirectToSignIn = () => {
    // chat 로그인 직후(receive=success) 복귀했는데도 쿠키를 못 읽는 경우,
    // 다시 chat으로 보내면 reward↔로그인 무한 루프가 된다. 안전망으로 재리다이렉트 차단.
    const isReceiveSuccess = new URLSearchParams(window.location.search).get('receive') === 'success';
    if (isReceiveSuccess) {
      return;
    }

    if (isAosDevice) {
      navigate(PATH.AUTH.SIGN_IN);
      return;
    }

    const redirectUrl = import.meta.env.VITE_APP_CHAT_URL;
    const _redirectUrl = import.meta.env.VITE_APP_ENV === 'local' ? 'http://localhost:3000' : redirectUrl;
    window.location.href = `${_redirectUrl}/sign-in?callbackUrl=${window.origin}`;
  };

  // 쿠키를 사용한 로그인 처리
  const handleLoginWithCookie = async (cookieToken: string) => {
    try {
      setTokenOnHeader(axiosInstance, auth, cookieToken);
      const res = await getCustomToken(http);
      if (!res?.token) {
        // 토큰을 받지 못한 경우도 인증 실패로 처리
        throw new Error('No custom token returned');
      }

      const userCredential = await signInWithCustomToken(auth, res.token);
      const idToken = await userCredential.user.getIdToken();
      await handleLoginInFirebaseAuth(userCredential.user, idToken);
    } catch (error) {
      // 토큰 audience 불일치(cross-env) 등으로 /auth/token 이 401 을 주면 여기로 온다.
      // 토큰을 정리하지 않고 멈추면 isCustomLogin=false 로 빈 화면에 갇히므로, 토큰 제거 후 로그인 페이지로 보낸다.
      console.error('Token validation failed:', error);
      clearAuthToken();
      localStorage.removeItem('isCustomLogin');
      setUser(null);
      redirectToSignIn();
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 현재 Firebase 인증 상태 감시
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          const cookieTokenUid = getCookieTokenUid();
          if (currentUser && cookieTokenUid && cookieTokenUid !== currentUser.uid && !hasSwitchedAccountRef.current) {
            // Firebase 세션은 오리진마다 따로 저장돼, 챗에서 계정을 바꿔도 리워드에는 이전 계정 세션이 남는다(#2625).
            // 공유 쿠키(챗의 현재 계정)와 다르면 이전 세션을 로그아웃 → 리스너가 null 로 다시 불려 쿠키 로그인으로 넘어간다.
            // isCustomLogin 을 먼저 지워야 요청 인터셉터가 /auth/token 에 이전 세션 토큰 대신 쿠키 토큰을 싣는다.
            hasSwitchedAccountRef.current = true;
            setUser(null);
            localStorage.removeItem('isCustomLogin');
            await signOut(auth);
          } else if (currentUser) {
            // 파이어베이스에 이미 로그인 여부가 파악된 경우
            const token = await currentUser.getIdToken();
            await handleLoginInFirebaseAuth(currentUser, token);
          } else {
            // Firebase에 로그인 되지 않은 경우, 쿠키로 인증 체크하기
            const cookieToken = getTokenFromCookie();
            if (cookieToken) {
              await handleLoginWithCookie(cookieToken);
            } else {
              // 쿠키도 없는 경우, 로그인 페이지로 이동
              setUser(null);
              // stale 플래그를 정리하지 않으면 로그인 페이지의 가드가 다시 대시보드로 보내 무한 리다이렉트 발생
              localStorage.removeItem('isCustomLogin');
              redirectToSignIn();
            }
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setLoading(false);
      }
    };

    initAuth();
  }, [auth, http, axiosInstance]);

  // 계정이 확정(user)되기 전에는 그리지 않는다. 먼저 그리면 하위 화면 요청이 이전 계정 세션 토큰을 싣고
  // 그 토큰으로 공유 쿠키까지 덮어써, 위 계정 전환이 쿠키에서 이전 계정을 다시 읽는다(#2625).
  if (!isCustomLogin || !user) {
    return null;
  }

  return children;
};

export default AuthGuard;
